import * as React from 'react'
import { Loader2 } from 'lucide-react'
import type { ApiKeyDto } from '@lib/dto/tenant-settings.dto'
import type { Uuid } from '@lib/validators/params'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toast } from '@/components/ui/toast'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'

interface RevokeKeyDialogProps {
  /** The key to revoke; the dialog is open while one is set. */
  apiKey: ApiKeyDto | null
  onClose: () => void
  onRevoke: (keyId: Uuid) => Promise<ApiKeyDto>
}

/** Confirms revocation: immediate and permanent. */
export function RevokeKeyDialog({ apiKey, onClose, onRevoke }: RevokeKeyDialogProps) {
  const [error, setError] = React.useState<string | null>(null)
  const [isPending, setIsPending] = React.useState(false)

  async function confirm(key: ApiKeyDto): Promise<void> {
    setError(null)
    setIsPending(true)
    try {
      await onRevoke(key.id)
      toast.success(`Revoked “${key.name}”`)
      onClose()
    } catch (failure) {
      setError(apiErrorMessage(failure, 'Could not revoke the key.'))
    } finally {
      setIsPending(false)
    }
  }

  return (
    <Dialog
      open={apiKey !== null}
      onOpenChange={(open) => {
        if (!open) {
          setError(null)
          onClose()
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Revoke “{apiKey?.name}”?</DialogTitle>
          <DialogDescription>
            Requests with <span className="font-mono">{apiKey?.prefix}…</span> fail from now on. This can&apos;t be
            undone; create a new key first if anything still uses it.
          </DialogDescription>
        </DialogHeader>
        {error ? <p className="text-body text-danger">{error}</p> : null}
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="destructive" disabled={isPending || !apiKey} onClick={() => apiKey && void confirm(apiKey)}>
            {isPending ? <Loader2 className="animate-spin" /> : null}
            Revoke key
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
